import {
  bindable,
  BindingEngine,
  computedFrom,
  Container, Disposable,
  DOM,
  LogManager,
  PLATFORM, View,
  ViewEngine
} from 'aurelia-framework';
import {inject} from 'aurelia-property-injection';
import {UserService} from '../../../services/user-service';
import {cloneDeep, debounce, noop, once, parseInt, pullAllBy} from 'lodash';
import {InputKeySelector} from '../../attributes/input-key-selector';
import {Paginator} from "../../../tools/http/paginator";
import {User} from "../../../services/user-service";

const logger = LogManager.getLogger('SmartSearchCustomAttribute');
// logger.setLevel(LogManager.logLevel.error);
logger.setLevel(LogManager.logLevel.info);

export class SmartSearchCustomAttribute {

  @inject(DOM.Element) element;
  @inject(Container) container;
  @inject(ViewEngine) viewEngine;
  @inject(BindingEngine) bindingEngine;
  @inject(UserService) userService;

  @bindable width = -1;
  @bindable height = -1;
  @bindable groupId = -1;
  @bindable pageSize = 10;
  @bindable onSelect = noop;
  @bindable previousSelected = [];

  noop = noop;
  userList: User[] = [];
  scrollTop = 0;
  highlightedIndex = -1;
  scrollDisabled = false;
  pgPromise = Promise.resolve();
  keySelector = new InputKeySelector({
    onUp: e => this.__moveHighlightUp(e),
    onDown: e => this.__moveHighlightDown(e),
    onSelect: e => this.__selectHighlighted(),
  });
  paginator: Paginator<User>;
  inputValueSubs: Disposable;
  blurListener: EventListener;
  focusListener: EventListener;
  keyListener: EventListener;

  private __emptyListBeforeFirstPage: Function;
  private __isVisible: boolean;
  view: View;

  listEl: HTMLUListElement;
  menuEl: HTMLMenuElement;

  @computedFrom('userList')
  get listSize() {
    return this.userList.length;
  }

  bind() {
    this.inputValueSubs = this.bindingEngine
      .propertyObserver(this.element, 'value')
      .subscribe(debounce((v1, v2) => {
        if (v1 !== v2) this.inputValueChanged(v1, v2);
      }, 600));

    this.element.addEventListener('blur', this.blurListener = () => {
      this.hide();
    });
    this.element.addEventListener('focus', this.focusListener = () => {
      if (this.element.value) this.show();
    });
    this.element.addEventListener('keydown', this.keyListener = e => {
      this.keySelector.handleKeyPress(e);
    });
  }

  unbind() {
    this.inputValueSubs.dispose();
    this.element.removeEventListener('focus', this.focusListener);
    this.element.removeEventListener('blur', this.blurListener);
    this.element.removeEventListener('keyup', this.keyListener);
    this.__destroyViewElement();
    this.__cancelPagination();
  }

  inputValueChanged(newValue = this.element.value, oldValue) {
    if (newValue) {
      this.show();

      this.scrollTop = 0;
      this.scrollDisabled = newValue.length < 3;

      this.highlightedIndex = -1;
      this.__cancelPagination();

      this.__emptyListBeforeFirstPage = once(() => this.__emptyList());
      this.paginator = this.userService.smartSearch({
        mask: newValue,
        groupId: this.groupId,
        pageNum: 1,
        pageSize: this.pageSize
      });

      this.__requestUserData();
    } else {
      this.hide();
      this.__emptyList();
    }
  }

  show() {
    let promise;

    if (!this.__isVisible) {
      promise = this.__createViewElement().then(() => {
        this.__isVisible = true;
        this.__initAppearance();
      });
    } else {
      promise = Promise.resolve();
    }

    return promise;
  }

  hide() {
    if (this.__isVisible) {
      this.__isVisible = false;
      this.__destroyViewElement();
    }
  }

  selectUser(index) {
    this.element.value = '';
    this.highlightedIndex = -1;

    this.hide();
    const event = new Event('change');
    event.initEvent('change', true, true);
    this.element.dispatchEvent(event);

    const user = cloneDeep(this.userList[index]);
    this.onSelect({user});
  }

  getMore() {
    this.pgPromise.then(() => this.__requestUserData());
  }

  onMenuResize() {
    const cs = DOM.getComputedStyle(this.listEl);
    const height = parseInt(cs.height);
    this.menuEl.style.height = `${height + 2}px`;
  }

  onWindowResize() {
    this.hide();
  }

  __emptyList() {
    this.userList = [];
  }

  __requestUserData() {
    this.pgPromise = this.paginator.next().then(users => {
      this.__emptyListBeforeFirstPage();
      this.userList = this.userList.concat(users);
      this.userList =  pullAllBy(this.userList, this.previousSelected, 'email');
      this.highlightedIndex = 0;
    }).catch(e => {
      if (e.responseType !== 'abort') {
        this.__emptyList();
        logger.error(e);
      }
    });
  }

  __cancelPagination() {
    if (this.paginator) this.paginator.cancel();
  }

  __createViewElement() {
    let template = PLATFORM.moduleName('resources/controls/smart-search/smart-search.html');
    return this.viewEngine.loadViewFactory(template).then(factory => {
      const childContainer = this.container.createChild();
      this.view = factory.create(childContainer);
      this.view.bind(this);

      DOM.appendNode(this.view.fragment);
      this.view.attached();
    });
  }

  __destroyViewElement() {
    if (this.view) {
      this.view.removeNodes();
      this.view.detached();
      this.view.unbind();
    }
  }

  __initAppearance() {
    const style = this.menuEl.style;
    const rect = this.element.getBoundingClientRect();
    style.width = this.width > 0 ? `${this.width}px` : `${rect.width}px`;
    style.height = this.height > 0 ? `${this.height}px` : '';
    style.top = `${rect.bottom}px`;
    style.left = `${rect.left}px`;
  }

  __moveHighlightDown(e) {
    e.preventDefault();
    if (this.highlightedIndex >= this.userList.length - 1 || this.highlightedIndex < 0) {
      this.highlightedIndex = 0;
    } else {
      this.highlightedIndex++;
    }
  }

  __moveHighlightUp(e) {
    e.preventDefault();
    if (this.highlightedIndex <= 0) {
      this.highlightedIndex = this.userList.length - 1;
    } else {
      this.highlightedIndex--;
    }
  }

  __selectHighlighted() {
    if (this.highlightedIndex < 0) return;
    this.selectUser(this.highlightedIndex);
  }

}
